<template>
	<form
		class="cart-footer-frame"
		:class="{ active: isActive }"
		@submit.prevent=""
	>
		<div class="cart-footer-container">
			<div
				v-if="isLocationRequestEnabled"
				class="cart-footer-block cart-footer-wrap"
			>
				<div class="location">
					<t-input
						:key="disabledLocationInput"
						debounced
						:value="currentLocation"
						:max-length="30"
						:error="!isLocationValid ? 'Location required' : ''"
						:error-icon-visible="false"
						label="Scan QR or enter location"
						:cleanable="!disabledLocationInput"
						:required="!isTabOpenedByRedemption"
						:readonly="disabledLocationInput"
						menu-style
						@input="changeLocation"
						@clean="currentLocation = ''"
					/>
				</div>
				<span
					class="qr"
					:class="{
						highlighted: isQRHighlighted,
						disabled: isTabOpenedWithLocation
					}"
					@click="showQrCodeModal"
				/>
			</div>
			<div
				v-if="isEnforceCollectEmailAndPhoneEnabled"
				class="cart-footer-block"
			>
				<t-phone-input
					:value="currentPhoneNumber"
					:error="isPhoneErrorShown ? phoneNumberError : ''"
					:error-icon-visible="false"
					cleanable
					menu-style
					@blur="changePhone"
					@clean="currentPhoneNumber = ''"
				/>
			</div>
			<div v-else class="cart-footer-block">
				<t-input
					debounced
					:value="currentCustomerName"
					:max-length="50"
					label="Name"
					:cleanable="!isTabOpened"
					:readonly="isTabOpened"
					menu-style
					@input="changeCustomerNameToPrint"
					@clean="currentCustomerName = ''"
				/>
			</div>

			<template v-if="isEnforceCollectEmailAndPhoneEnabled">
				<div class="cart-footer-block">
					<t-input
						debounced
						:value="currentEmail"
						label="Email"
						cleanable
						:error="emailError"
						:error-icon-visible="false"
						required
						menu-style
						@input="changeEmail"
						@clean="currentEmail = ''"
					/>
				</div>
				<div class="cart-footer-block">
					<t-input
						debounced
						:value="currentCustomerName"
						:max-length="50"
						label="Name"
						:cleanable="!isTabOpened"
						:readonly="isTabOpened"
						required
						menu-style
						@input="changeCustomerNameToPrint"
						@clean="currentCustomerName = ''"
					/>
				</div>
			</template>

			<custom-check-box
				v-if="isUtensilsVisible"
				id="in-venue-utensils"
				class="cart-footer-utensils"
				:disabled="!isTabEmpty && isTabOpened"
				:label="taxSettings.utensilsText || 'Utensils'"
				:checked="includeUtensils"
				@change="changeUtensils"
			/>
		</div>

		<div class="cart-footer-buttons-holder ">
			<primary-button
				:disabled="isButtonsDisabled"
				:loading="isLoading"
				large
				@click="checkout"
			>
				<template>
					{{ isTabOpened ? 'Update' : 'Checkout' }}
					<template v-if="totalProductsCount">
						<span class="ml-8 mr-8">-</span>
						<formatted-price :price="totalPrice" decimal-height="70" />
					</template>
				</template>
			</primary-button>
		</div>
		<portal v-if="isActive" to="modal">
			<transition name="bottom-slide">
				<credit-card-modal
					v-if="isShowCreditCard"
					:amount="totalPrice"
					@credit-card-added="creditCardAdded"
					@close="hideCreditCardModal"
				/>
				<qr-code-scanner-modal
					v-if="isShowQrCodeScanner"
					@qr-code-scanned="qrCodeScanned"
					@close="hideQrCodeModal"
				/>
			</transition>
			<transition name="fade">
				<payment-notice-modal
					v-if="isPaymentNoticeModalVisible"
					dark-theme
					@close="hidePaymentNoticeModal"
				/>
			</transition>
			<transition name="fade">
				<unavailability-modal
					v-if="isUnAvailableModalOpen"
					v-bind="unAvailableModalProps"
					@on-action="onModalAction"
					@close="closeUnAvailableModal"
				/>
			</transition>
			<transition name="fade">
				<mandatory-order-requirements-modal
					v-if="isMandatoryModalOpen"
					dark-theme
					:condition="mandatoryCondition"
					@close="isMandatoryModalOpen = false"
				/>
			</transition>
		</portal>
	</form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import debounce from 'lodash/debounce'

import CreditCardModal from '@/components/modals/CreditCardModal'
import QrCodeScannerModal from '@/components/modals/QRCodeScannerModal'
import PaymentNoticeModal from '@/components/modals/PaymentNoticeModal'
import UnavailabilityModal from '@/components/modals/UnavailabilityModal.vue'
import MandatoryOrderRequirementsModal from '@/components/modals/MandatoryOrderRequirementsModal.vue'
import CustomCheckBox from '@/components/forms/CustomCheckBox.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import TInput from '@/components/Ticketing/Controls/TInput.vue'
import TPhoneInput from '@/components/Ticketing/Controls/TPhoneInput.vue'

import storage from '@/utils/storage'
import { emailValidation, scrollToBottom } from '@/utils/helpers'
import routerMixin from '@/mixins/routerMixin'

import {
	ORDER_LIMITATION_CONDITION,
	MIN_ORDER_AMOUNT,
	PRODUCT_ORDER_TYPES,
	DEFAULT_UNAVAILABLE_MODAL_PROPS,
	ALL_ORDER_TYPES_NAMES,
	ORDERS_ACTIONS_TEXT_LIST
} from '@/constants'
import { VueMaskFilter } from 'v-mask'
import FormattedPrice from '@/components/FormattedPrice.vue'

export default {
	name: 'CartFooterInVenue',

	mixins: [routerMixin],

	components: {
		FormattedPrice,
		TPhoneInput,
		TInput,
		PrimaryButton,
		CustomCheckBox,
		UnavailabilityModal,
		CreditCardModal,
		QrCodeScannerModal,
		PaymentNoticeModal,
		MandatoryOrderRequirementsModal
	},

	props: {
		isActive: {
			type: Boolean,
			default: false
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		beforeCheckout: {
			type: Function,
			default: () => {}
		}
	},

	data() {
		return {
			currentLocation: '',
			currentPhoneNumber: '',
			currentEmail: '',
			currentCustomerName: '',
			currentIncludeUtensils: '',
			isShowCreditCard: false,
			isShowQrCodeScanner: false,
			isLocationValid: true,
			phoneNumberError: '',
			isPhoneErrorShown: false,
			isEmailValid: true,
			isCustomerNameValid: true,
			isPaymentNoticeModalVisible: false,
			isUnAvailableModalOpen: false,
			unAvailableModalProps: { ...DEFAULT_UNAVAILABLE_MODAL_PROPS },
			isMandatoryModalOpen: false,
			mandatoryCondition: ''
		}
	},

	computed: {
		...mapState('venue', [
			'venueInfo',
			'isForHereOrderingAvailable',
			'forHereHours'
		]),
		...mapState('cart', [
			'creditCardId',
			'location',
			'email',
			'tableId',
			'customerNameToPrint',
			'cartInfoMessage',
			'includeUtensils',
			'phoneNumber',
			'cartItems',
			'isCartRecreated'
		]),
		...mapState('cart', { cartId: 'id' }),
		...mapGetters('cart', [
			'totalPrice',
			'totalProductsCount',
			'cartHasFood',
			'cartHasBeverage',
			'isToGoProductsIncludesFood',
			'isTabOpened',
			'isTabEmpty',
			'openedTab',
			'isTabOpenedByRedemption'
		]),

		emailError() {
			if (!this.isEmailValid) {
				return this.currentEmail ? 'Check your email' : 'Email required'
			}

			return ''
		},

		isUtensilsVisible() {
			const { utensilsForToGo } = this.taxSettings

			return utensilsForToGo && this.isToGoProductsIncludesFood
		},

		isTabOpenedWithLocation() {
			return Boolean(this.isTabOpened && this.openedTab.location)
		},

		disabledLocationInput() {
			return this.isTabOpenedWithLocation || this.isTableSelected
		},

		taxSettings() {
			return this.venueSettings.taxSettings
		},

		venueSettings() {
			return this.venueInfo.settings
		},

		isEnforceCollectEmailAndPhoneEnabled() {
			return this.venueSettings.isEnforceCollectEmailAndPhoneEnabled
		},

		isTableSelected() {
			return !!(this.tableId && this.currentLocation)
		},

		isButtonsDisabled() {
			return !this.totalProductsCount || this.isLoading
		},

		isQRHighlighted() {
			return !this.isLocationValid && !this.currentLocation
		},

		isLocationRequestEnabled() {
			return this.venueSettings.isLocationRequestEnabled
		}
	},

	watch: {
		isActive(val) {
			if (val) {
				this.init()

				const isValid = this.validation({
					isSilent: true
				})

				if (isValid) {
					this.callUpdateCart()
				} else {
					this.$emit('expand-footer')
				}
			}
		},

		cartItems() {
			this.productsValidation()
		},

		isLoading(val) {
			this.unAvailableModalProps = {
				...this.unAvailableModalProps,
				isLoading: val
			}
		}
	},

	created() {
		this.init()
	},

	methods: {
		...mapActions('venue', ['setAllOrderingAvailable']),
		...mapActions('cart', [
			'getCart',
			'updateCart',
			'openTab',
			'updateTab',
			'setCartInfoMessage',
			'toggleIsCartRecreated'
		]),
		...mapActions('ui', ['addMainError']),

		init() {
			this.currentLocation = this.location
			this.currentCustomerName = this.customerNameToPrint
			this.currentIncludeUtensils = this.includeUtensils

			if (this.isEnforceCollectEmailAndPhoneEnabled) {
				this.currentPhoneNumber = this.getClearPhoneNumber()
				this.currentEmail = this.email || storage.getStorage('email')
			}
			this.currentCustomerName =
				this.customerNameToPrint || storage.getStorage('name')
		},

		getClearPhoneNumber() {
			const phoneNumber =
				this.phoneNumber?.replace('+1', '') || storage.getStorage('phone')

			return phoneNumber || ''
		},

		showQrCodeModal() {
			this.isShowQrCodeScanner = true
		},

		hideQrCodeModal() {
			this.isShowQrCodeScanner = false
		},

		showCreditCardModal() {
			this.isShowCreditCard = true
		},

		hideCreditCardModal() {
			this.isShowCreditCard = false
		},

		showPaymentNoticeModal() {
			this.isPaymentNoticeModalVisible = true
		},

		hidePaymentNoticeModal() {
			this.isPaymentNoticeModalVisible = false
		},

		changeLocation(location) {
			if (this.currentLocation === location.trim()) return

			this.currentLocation = location && location.trim()

			if (!this.isLocationValid) {
				this.isLocationValid = this.locationValidation()
			}

			this.callDeferredUpdateCart({
				location: this.currentLocation,
				tableId: null
			})
		},

		async qrCodeScanned({ location, tableId }) {
			this.currentLocation = location && location.trim()
			this.isShowQrCodeScanner = false

			if (!this.isLocationValid) {
				this.isLocationValid = this.locationValidation()

				if (this.isLocationValid) {
					await this.updateLocationByQrCode(location, tableId)
				}
			} else {
				await this.updateLocationByQrCode(location, tableId)
			}
		},

		async updateLocationByQrCode(location, tableId) {
			try {
				await this.callUpdateCart({
					tableId,
					location
				})
			} catch (e) {
				this.isShowQrCodeScanner = false
			}
		},

		changeCustomerNameToPrint(name) {
			if (this.currentCustomerName === name?.trim()) return

			this.currentCustomerName = name && name.trim()
			storage.setStorage('name', this.currentCustomerName)

			this.callDeferredUpdateCart({
				customerNameToPrint: this.currentCustomerName
			})
		},

		changeEmail(email) {
			if (this.currentEmail === email?.trim()) return

			this.currentEmail = email && email.trim()
			storage.setStorage('email', this.currentEmail)

			const isValid = this.emailValidation()

			if (isValid) {
				this.callDeferredUpdateCart()
				this.isEmailValid = true
			}
		},

		changePhone(phoneNumber) {
			if (phoneNumber === this.currentPhoneNumber) return

			this.currentPhoneNumber = phoneNumber
			storage.setStorage('phone', this.currentPhoneNumber)

			this.validatePhone(phoneNumber)

			if (this.phoneNumberError) return

			this.callDeferredUpdateCart()
		},

		callDeferredUpdateCart: debounce(async function(params) {
			this.callUpdateCart({ ...params, isNeedShowExpiredPopup: true })
		}, 300),

		async creditCardAdded(creditCard) {
			this.setIsLoading(true)

			await this.getCart(true)

			if (this.isCartRecreated) {
				this.setIsLoading(false)

				return
			}

			try {
				await this.callUpdateCart({ creditCardId: creditCard.id })
			} catch (e) {
				this.hideCreditCardModal()
				this.setIsLoading(false)
				return
			}

			await this.callOpenTab()

			this.setIsLoading(false)
		},

		changeUtensils(includeUtensils) {
			this.currentIncludeUtensils = includeUtensils
			this.callUpdateCart({ includeUtensils, isNeedShowExpiredPopup: true })
		},

		async callUpdateCart(params = {}) {
			try {
				await this.updateCart({
					location: this.currentLocation || this.location,
					email: this.currentEmail || this.email,
					includeUtensils: this.currentIncludeUtensils,
					customerNameToPrint:
						this.currentCustomerName || this.customerNameToPrint,
					phoneNumber: this.getPhoneForRequest(this.currentPhoneNumber),
					...params
				})
			} catch (e) {
				throw new Error(e)
			}
		},

		checkAvailability() {
			this.unAvailableModalProps = { ...DEFAULT_UNAVAILABLE_MODAL_PROPS }

			if (this.isForHereOrderingAvailable) {
				return true
			} else {
				this.unAvailableModalProps = {
					...this.unAvailableModalProps,
					actionText: ORDERS_ACTIONS_TEXT_LIST.cancel,
					action: this.deleteOrder,
					orderType: ALL_ORDER_TYPES_NAMES.forHere,
					availablePeriod: this.forHereHours?.length && this.forHereHours[0]
				}

				return false
			}
		},

		deleteOrder() {
			this.$emit('delete-order')
		},

		onModalAction(action) {
			action()
		},

		closeUnAvailableModal() {
			this.isUnAvailableModalOpen = false
		},

		async checkout() {
			if (this.totalPrice < MIN_ORDER_AMOUNT) {
				return this.showPaymentNoticeModal()
			}

			const isValid = this.validation({
				isSilent: false
			})

			if (!isValid) {
				scrollToBottom()

				return
			}

			try {
				this.setIsLoading(true)
				await this.beforeCheckout()
			} catch (e) {
				this.setIsLoading(false)
			}

			const storeCartId = storage.getStorage('cartId')

			await this.getCart()

			if (this.cartId && storeCartId && this.cartId !== storeCartId) {
				this.toggleIsCartRecreated()
				this.setIsLoading(false)

				return
			}

			const isAvailable = this.checkAvailability()

			if (!isAvailable) {
				this.setIsLoading(false)
				this.isUnAvailableModalOpen = true

				return
			}

			this.callOpenTab()
		},

		async callOpenTab() {
			try {
				this.setIsLoading(true)

				await this.getCart()

				if (this.isCartRecreated) {
					this.setIsLoading(false)
					return
				}

				if (!this.creditCardId) {
					this.setIsLoading(false)
					this.showCreditCardModal()
					return
				}

				if (this.isTabOpened) {
					await this.updateTab({
						cartId: this.cartId
					})
				} else {
					await this.openTab({
						cartId: this.cartId
					})
				}
			} catch (e) {
				this.setIsLoading(false)
			}
		},

		validation({ isSilent }) {
			this.setAllOrderingAvailable()

			const isProductsValid = this.productsValidation()

			if (!isProductsValid) {
				this.isMandatoryModalOpen = true

				return
			}

			const isLocationValid = this.locationValidation()
			let isEmailValid = true

			if (this.isEnforceCollectEmailAndPhoneEnabled) {
				isEmailValid = this.emailValidation()
			}

			if (!isSilent) {
				this.isProductsValid = isProductsValid
				this.isLocationValid = isLocationValid

				if (this.isEnforceCollectEmailAndPhoneEnabled) {
					this.isEmailValid = isEmailValid
					this.validatePhone(this.currentPhoneNumber, true)
				}
			} else {
				if (this.isEnforceCollectEmailAndPhoneEnabled) {
					this.validatePhone(this.currentPhoneNumber)
				}
			}

			if (this.phoneNumberError && this.isEnforceCollectEmailAndPhoneEnabled) {
				return false
			}

			return isProductsValid && isLocationValid && isEmailValid
		},

		productsValidation() {
			const { orderLimCondition, orderLimForHere } = this.venueInfo.settings
			let message = null
			let isProductsValid = true

			const isForHereProducts = this.cartItems.some(
				item => item.orderType === PRODUCT_ORDER_TYPES.forHere
			)

			const isMandatoryCheckNeeded = orderLimForHere && isForHereProducts

			if (isMandatoryCheckNeeded) {
				switch (orderLimCondition) {
					case ORDER_LIMITATION_CONDITION.ORDER_HAS_AT_LIST_ONE_FOOD:
						if (!this.cartHasFood) {
							this.mandatoryCondition = 'At least 1 food item'

							message = {
								title: 'Your order must include',
								subTitle: this.mandatoryCondition
							}
							isProductsValid = false
						}
						break
					case ORDER_LIMITATION_CONDITION.ORDER_HAS_AT_LIST_ONE_DRINK:
						if (!this.cartHasBeverage) {
							this.mandatoryCondition = 'At least 1 drink item'

							message = {
								title: 'Your order must include',
								subTitle: this.mandatoryCondition
							}
							isProductsValid = false
						}
						break
					default:
						break
				}
			}

			this.setCartInfoMessage(message)
			return isProductsValid
		},

		locationValidation() {
			if (
				(this.isTabOpened && !this.openedTab.location) ||
				!this.isLocationRequestEnabled
			)
				return true

			return this.currentLocation && !!this.currentLocation.length
		},

		getFormattedPhone(phone) {
			return VueMaskFilter(phone, '#'.repeat(13))
		},

		validatePhone(maskedPhone, isPhoneErrorShown = false) {
			this.phoneNumberError = ''
			const phone = this.getFormattedPhone(maskedPhone)
			this.isPhoneErrorShown = isPhoneErrorShown

			if (!phone) {
				this.phoneNumberError = `Phone required`

				return
			}

			if (
				phone.length < 10 ||
				phone.length > 12 ||
				(phone.length !== 11 && phone[0] === '1')
			) {
				this.phoneNumberError = `Check your phone number`
			}
		},

		getPhoneForRequest(phone) {
			if (!phone) return ''

			const formattedPhone = this.getFormattedPhone(phone)
			return formattedPhone.length === 10
				? `+1${formattedPhone}`
				: `+${formattedPhone}`
		},

		emailValidation() {
			return emailValidation(this.currentEmail)
		},

		setIsLoading(val) {
			this.$emit('set-is-loading', val)
		}
	}
}
</script>
